import { useState, useEffect } from "react";
import { 
  Home, 
  Briefcase, 
  Users, 
  Map, 
  Euro, 
  HelpCircle, 
  MessageCircle,
  LayoutDashboard,
  LogIn,
  LogOut,
  Settings,
  UserPlus,
  Target,
  FileText,
  BarChart3,
  Lock,
  User,
  CreditCard,
  ShieldCheck,
  Volume2,
  Zap,
  BookOpen,
  Smartphone,
  GitBranch,
  Globe,
  ChevronDown,
  ChevronRight,
  Plus,
  Brain,
  History,
  Star,
  Info,
  FileCheck,
  Cookie,
  ScrollText,
  Scale,
  Send,
  FlaskConical,
  Calendar as CalendarIcon,
  ClipboardList,
  LucideIcon,
  Rocket,
  Search,
  Building2,
  FileSearch
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { PhoneLoginDialog } from "@/components/auth/PhoneLoginDialog";
import { MobileSubmenuPanel } from "@/components/MobileSubmenuPanel";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

// Interface pour les sous-menus mobiles
interface MobileSubmenu {
  title: string;
  items: { title: string; url: string; icon: LucideIcon }[];
}

// =====================================
// SECTION 1 — Utilisateur
// =====================================
const userNavItems = [
  { title: "Accueil", url: "/", icon: Home },
  { title: "Décrire mon besoin", url: "/missions", icon: Send },
  { title: "Devenir prestataire", url: "/prestataires", icon: UserPlus },
];

// =====================================
// SECTION 2 — Découverte & Info
// =====================================
const discoveryNavItems = [
  { title: "Comment ça marche", url: "/comment-ca-marche", icon: HelpCircle },
  { title: "Carte", url: "/carte", icon: Map },
  { title: "Tarifs", url: "/tarifs", icon: Euro },
  { title: "À propos", url: "/a-propos", icon: Info },
];

// =====================================
// SECTION 3 — Ressources
// =====================================
const resourcesNavItems = [
  { title: "FAQ", url: "/faq-pro", icon: HelpCircle },
  { title: "Lexique", url: "/lexique", icon: BookOpen },
  { title: "Contact", url: "/contact", icon: MessageCircle },
];

// =====================================
// SECTION 4 — Légal (sous-menu repliable)
// =====================================
const legalNavItems = [
  { title: "Mentions légales", url: "/mentions-legales", icon: FileCheck },
  { title: "CGU", url: "/cgu", icon: ScrollText },
  { title: "CGV", url: "/cgv", icon: Scale },
  { title: "Politique de confidentialité", url: "/rgpd", icon: ShieldCheck },
  { title: "Politique de cookies", url: "/cookies", icon: Cookie },
  { title: "Charte du prestataire", url: "/charte-prestataire", icon: FileText },
];

// =====================================

// Navigation items pour clients connectés
const clientNavItems = [
  { title: "Mon Dashboard", url: "/dashboard-client", icon: LayoutDashboard },
];

// Sous-menu "Mes demandes" pour clients
const clientDemandesSubmenu = {
  title: "Mes demandes",
  icon: ClipboardList,
  items: [
    { title: "Nouvelle demande", url: "/client/add-mission-ia", icon: Plus },
    { title: "Historique", url: "/client/recap-missions", icon: History },
  ]
};

// Navigation items pour l'espace personnel client (items hors sous-menu)
const clientPersonalItems = [
  { title: "Mes informations", url: "/client/mes-infos", icon: User },
  { title: "Carte", url: "/carte", icon: Map },
  { title: "Mot de passe", url: "/client/mot-de-passe", icon: Lock },
  { title: "Support", url: "/client/support", icon: MessageCircle },
];

// Navigation items pour prestataires connectés
const prestataireNavItems = [
  { title: "Mon Dashboard", url: "/dashboard-prestataire", icon: LayoutDashboard },
];

// Navigation items pour l'espace personnel prestataire (items simples)
const prestatairePersonalItems = [
  { title: "Mes infos", url: "/prestataire/mes-infos", icon: User },
  { title: "Mes compétences", url: "/prestataire/mes-competences", icon: Settings },
  { title: "Mes contraintes", url: "/prestataire/mes-contraintes", icon: Target },
  { title: "Calendrier", url: "/prestataire/calendrier", icon: CalendarIcon },
  { title: "Profil enrichi", url: "/prestataire/profil-enrichi", icon: Star },
  { title: "Missions", url: "/prestataire/missions", icon: Briefcase },
  { title: "Carte", url: "/carte", icon: Map },
  { title: "Mot de passe", url: "/prestataire/mot-de-passe", icon: Lock },
  { title: "Support", url: "/prestataire/support", icon: MessageCircle },
];

// Sous-menu Estimation IA pour prestataires
const estimationIASubmenu = {
  title: "Estimation IA",
  icon: Zap,
  items: [
    { title: "Créer une estimation", url: "/prestataire/estimation-add", icon: Plus },
    { title: "Déjà réalisées", url: "/prestataire/estimation-gestion", icon: History },
  ]
};

// Navigation items admin - structure hiérarchique réorganisée par profils et familles
const adminMenuStructure = [
  // =====================================
  // SECTION PILOTAGE (Dashboard principal)
  // =====================================
  {
    title: "Pilotage",
    icon: LayoutDashboard,
    defaultOpen: true,
    items: [
      { title: "Dashboard", url: "/dashboard-admin", icon: LayoutDashboard },
      { title: "Recherche historique", url: "/admin/recherche-historique", icon: History },
    ]
  },
  // =====================================
  // SECTION PAR PROFILS
  // =====================================
  {
    title: "Clients",
    icon: Users,
    defaultOpen: false,
    items: [
      { title: "Liste clients", url: "/admin/clients", icon: Users },
      { title: "Support clients", url: "/admin/support", icon: MessageCircle },
    ]
  },
  {
    title: "Prestataires",
    icon: UserPlus,
    defaultOpen: false,
    items: [
      { title: "Liste prestataires", url: "/admin/prestataires", icon: UserPlus },
      { title: "Partenaires Certifiés", url: "/admin/profil-partenaire", icon: Star },
      { title: "Estimation IA", url: "/admin/estimation-ia", icon: Brain },
      { title: "Calendrier", url: "/admin/calendrier", icon: CalendarIcon },
      { title: "Carte", url: "/carte", icon: Map },
      { title: "Documents", url: "/admin/documents", icon: FileText },
    ]
  },
  {
    title: "Missions",
    icon: Target,
    defaultOpen: false,
    items: [
      { title: "Liste missions", url: "/admin/missions", icon: Target },
      { title: "Embedding", url: "/admin/embedding", icon: Zap },
    ]
  },
  {
    title: "Équipe Admin",
    icon: ShieldCheck,
    defaultOpen: false,
    items: [
      { title: "Collaborateurs", url: "/admin/admins", icon: ShieldCheck },
      { title: "Mot de Passe", url: "/admin/password", icon: Lock },
    ]
  },
  // =====================================
  // SECTION COMMERCE & MARKETING
  // =====================================
  {
    title: "Commercial",
    icon: Euro,
    defaultOpen: false,
    items: [
      { title: "Tarifs", url: "/admin/tarifs", icon: Euro },
      { title: "RBA Tarifs", url: "/admin/rba", icon: Zap },
      { title: "Rapports Solvabilité", url: "/admin/solvability-reports", icon: FileSearch },
      { title: "Crédits Solvabilité", url: "/admin/credits-solvabilite", icon: CreditCard },
    ]
  },
  {
    title: "Marketing",
    icon: Rocket,
    defaultOpen: false,
    items: [
      { title: "Pré-Lancement", url: "/admin/marketing", icon: Rocket },
    ]
  },
  // =====================================
  // SECTION CONTENUS
  // =====================================
  {
    title: "Contenus",
    icon: FileText,
    defaultOpen: false,
    items: [
      { title: "Annonces", url: "/admin/annonces", icon: Volume2 },
      { title: "FAQ", url: "/admin/faq", icon: HelpCircle },
      { title: "Lexique", url: "/admin/lexique", icon: BookOpen },
      { title: "Documents légaux", url: "/admin/legal-documents", icon: ScrollText },
    ]
  },
  // =====================================
  // SECTION ANALYSE & STATS
  // =====================================
  {
    title: "Analyse",
    icon: BarChart3,
    defaultOpen: false,
    items: [
      { title: "Statistiques", url: "/admin/stats", icon: BarChart3 },
      { title: "Analytics", url: "/admin/analytics", icon: BarChart3 },
    ]
  },
  // =====================================
  // SECTION TECHNIQUE
  // =====================================
  {
    title: "Technique",
    icon: Settings,
    defaultOpen: false,
    items: [
      { title: "Voix IA", url: "/admin/voice-config", icon: Volume2 },
      { title: "Audio", url: "/admin/audio", icon: Volume2 },
      { title: "Mode Test SMS", url: "/admin/test-mode", icon: FlaskConical },
      { title: "Outils Système", url: "/admin/system-tools", icon: Settings },
    ]
  },
  // =====================================
  // SECTION PROSPECTION
  // =====================================
  {
    title: "Prospection",
    icon: Search,
    defaultOpen: false,
    items: [
      { title: "Recherche Entreprises", url: "/admin/recherche-entreprises", icon: Search },
      { title: "Nouvelles Entreprises", url: "/admin/prospection-nouvelles-entreprises", icon: Building2 },
      { title: "Vérifications SIRET", url: "/admin/verifications-siret", icon: FileSearch },
      { title: "Représentants Suspects", url: "/admin/representants-suspects", icon: ShieldCheck },
    ]
  },
  // =====================================
  // SECTION PARAMÈTRES
  // =====================================
  {
    title: "Paramètres",
    icon: Settings,
    defaultOpen: false,
    items: [
      { title: "Install App", url: "/install", icon: Smartphone },
    ]
  },
];

export function AppSidebar() {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const [showPhoneLogin, setShowPhoneLogin] = useState(false);
  
  const [openLegalMenu, setOpenLegalMenu] = useState(false);
  const [openEstimationMenu, setOpenEstimationMenu] = useState(false);
  const [openDemandesMenu, setOpenDemandesMenu] = useState(false);
  // État pour le sous-menu mobile (navigation à 2 niveaux)
  const [mobileSubmenu, setMobileSubmenu] = useState<MobileSubmenu | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, initialized, signOut, roles, userData, getActiveRole, getFirstName, getLastName } = useAuth();
  
  const currentPath = location.pathname;

  // Fermer le sous-menu mobile quand on change de page et ouvrir les menus pertinents
  useEffect(() => {
    setMobileSubmenu(null);
    // Auto-ouvrir le menu Estimation IA si on est sur une page d'estimation
    if (location.pathname.startsWith('/prestataire/estimation')) {
      setOpenEstimationMenu(true);
    }
    // Auto-ouvrir le menu Mes demandes si on est sur une page client missions
    if (location.pathname.startsWith('/client/add-mission') || location.pathname.startsWith('/client/recap-missions')) {
      setOpenDemandesMenu(true);
    }
  }, [location.pathname]);


  // Use centralized getActiveRole from useAuth
  const effectiveRole = getActiveRole();

  const getNavigationItems = (): {
    authenticated?: typeof prestataireNavItems;
    prestatairePersonal?: typeof prestatairePersonalItems;
    clientPersonal?: typeof clientPersonalItems;
    adminMenus?: typeof adminMenuStructure;
    isPublic: boolean;
  } => {
    if (!initialized) return { authenticated: [], isPublic: true };
    
    const role = effectiveRole;
    
    if (isAuthenticated()) {
      if (role === 'admin') {
        return { 
          adminMenus: adminMenuStructure,
          isPublic: false
        };
      } else if (role === 'prestataire') {
        return { 
          authenticated: prestataireNavItems, 
          prestatairePersonal: prestatairePersonalItems,
          isPublic: false
        };
      } else if (role === 'client') {
        return { 
          authenticated: clientNavItems, 
          clientPersonal: clientPersonalItems,
          isPublic: false
        };
      }
      return { authenticated: [], isPublic: false };
    }
    
    return { authenticated: [], isPublic: true };
  };

  const navigationItems = getNavigationItems();
  
  const isActive = (path: string) => {
    if (path === "/") return currentPath === "/";
    return currentPath.startsWith(path);
  };

  const getNavClassName = (path: string) => {
    return isActive(path) 
      ? "bg-[#F57C00]/20 text-[#F57C00] font-medium" 
      : "text-gray-300 hover:bg-white/10 hover:text-white";
  };

  const getInitials = () => {
    const firstName = userData?.first_name || getFirstName();
    const lastName = userData?.last_name || getLastName();
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return "U";
  };

  const getUserRoleLabel = () => {
    if (effectiveRole === 'admin') return 'Administrateur';
    if (effectiveRole === 'prestataire') return 'Prestataire';
    if (effectiveRole === 'client') return 'Client';
    return null;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleNavigation = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Render pour les menus publics (non connectés)
  const renderPublicSidebar = () => (
    <>
      {/* SECTION 1: Utilisateur */}
      <SidebarGroup data-onboarding="sidebar-section-utilisateur">
        <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
          Utilisateur
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {userNavItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                  <NavLink 
                    to={item.url}
                    end={item.url === "/"}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                    onClick={handleNavigation}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      {/* SECTION 2: Découverte & Info */}
      <SidebarGroup data-onboarding="sidebar-section-decouverte">
        <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
          Découverte & Info
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {discoveryNavItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                  <NavLink 
                    to={item.url}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                    onClick={handleNavigation}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* SECTION 3: Ressources */}
      <SidebarGroup data-onboarding="sidebar-section-ressources">
        <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
          Ressources
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {resourcesNavItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                  <NavLink 
                    to={item.url}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                    onClick={handleNavigation}
                  >
                    <item.icon className="h-5 w-5 shrink-0" />
                    {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* SECTION 4: Légal - Navigation à 2 niveaux sur mobile */}
      <SidebarGroup data-onboarding="sidebar-section-legal">
        <SidebarMenu>
          {/* VERSION MOBILE: Bouton qui ouvre le panneau secondaire */}
          {isMobile ? (
            <SidebarMenuItem>
              <SidebarMenuButton 
                onClick={() => setMobileSubmenu({ title: "DOCUMENTS LÉGAUX", items: legalNavItems })}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200"
              >
                <span className="text-xs font-semibold uppercase tracking-wider text-[#F57C00]">Documents légaux</span>
                <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            /* VERSION DESKTOP: Menu Collapsible classique */
            <Collapsible
              open={openLegalMenu}
              onOpenChange={setOpenLegalMenu}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton 
                    tooltip={state === "collapsed" ? "DOCUMENTS LÉGAUX" : undefined}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""}`}
                  >
                    {state !== "collapsed" && (
                      <>
                        <span className="text-xs font-semibold uppercase tracking-wider text-[#F57C00]">Documents légaux</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 text-gray-400" />
                      </>
                    )}
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className="border-l border-white/10 ml-6 pl-3 mt-1">
                    {legalNavItems.map((item) => (
                      <SidebarMenuSubItem key={item.title}>
                        <SidebarMenuSubButton asChild>
                          <NavLink 
                            to={item.url}
                            className={`flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-all duration-200 ${getNavClassName(item.url)}`}
                            onClick={handleNavigation}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            {state !== "collapsed" && <span>{item.title}</span>}
                          </NavLink>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )}
        </SidebarMenu>
      </SidebarGroup>
    </>
  );

  // État pour les menus admin ouverts sur mobile (comportement accordion)
  const [openAdminMenus, setOpenAdminMenus] = useState<string[]>([]);

  const toggleAdminMenu = (menuTitle: string) => {
    // Comportement accordion: ferme les autres menus quand on en ouvre un
    setOpenAdminMenus(prev => 
      prev.includes(menuTitle) 
        ? prev.filter(m => m !== menuTitle) 
        : [menuTitle]
    );
  };

  // Auto-ouvrir le menu contenant la route active
  useEffect(() => {
    if (isMobile && effectiveRole === 'admin') {
      const activeMenu = adminMenuStructure.find(menu => 
        menu.items.some(item => currentPath.startsWith(item.url))
      );
      if (activeMenu) {
        setOpenAdminMenus([activeMenu.title]);
      }
    }
  }, [currentPath, isMobile, effectiveRole]);

  // Render pour les utilisateurs connectés (admin, client, prestataire)
  const renderAuthenticatedSidebar = () => (
    <>
      {/* Menus Admin avec structure collapsible sur mobile - AFFICHAGE UNE SEULE COLONNE */}
      {navigationItems.adminMenus && navigationItems.adminMenus.length > 0 && (
        <div className="flex flex-col w-full">
          {navigationItems.adminMenus.map((menu) => (
            <SidebarGroup 
              key={menu.title}
              data-onboarding={`sidebar-admin-${menu.title.toLowerCase().replace(/[éè]/g, 'e').replace(/\s+/g, '-')}`}
              className="w-full"
            >
              {/* Sur mobile: menu collapsible accordion en colonne unique */}
              {isMobile ? (
                <Collapsible
                  open={openAdminMenus.includes(menu.title)}
                  onOpenChange={() => toggleAdminMenu(menu.title)}
                  className="group/collapsible w-full"
                >
                  <CollapsibleTrigger asChild>
                    <SidebarGroupLabel className="cursor-pointer flex items-center justify-between w-full text-[#F57C00] text-xs font-semibold uppercase tracking-wider hover:bg-white/5 rounded-lg px-3 py-2.5 transition-colors">
                      <div className="flex items-center gap-2">
                        <menu.icon className="h-4 w-4" />
                        <span>{menu.title}</span>
                      </div>
                      <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 text-gray-400" />
                    </SidebarGroupLabel>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="w-full">
                    <SidebarGroupContent className="pl-4">
                      <SidebarMenu>
                        {menu.items.map((item) => (
                          <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild>
                              <NavLink
                                to={item.url}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 w-full ${getNavClassName(item.url)}`}
                                onClick={handleNavigation}
                              >
                                <item.icon className="h-5 w-5 shrink-0" />
                                <span className="text-sm">{item.title}</span>
                              </NavLink>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <>
                  <SidebarGroupLabel className={`${state === "collapsed" ? "text-[10px] text-center px-1" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
                    {state === "collapsed" ? menu.title.slice(0, 6) : menu.title}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {menu.items.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                            <NavLink
                              to={item.url}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                              onClick={handleNavigation}
                            >
                              <item.icon className="h-5 w-5 shrink-0" />
                              {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </>
              )}
            </SidebarGroup>
          ))}
        </div>
      )}

      {/* Navigation Dashboard + Profil pour prestataires (combiné) */}
      {effectiveRole === 'prestataire' && navigationItems.authenticated && navigationItems.authenticated.length > 0 && navigationItems.prestatairePersonal && (
        <SidebarGroup>
          <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
            Mon Espace
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Dashboard */}
              {navigationItems.authenticated.map((item) => (
                <SidebarMenuItem key={item.title} data-onboarding="sidebar-presta-dashboard">
                  <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                    <NavLink 
                      to={item.url}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                      onClick={handleNavigation}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {/* Profil items (premiers 4) */}
              {navigationItems.prestatairePersonal.slice(0, 4).map((item, idx) => (
                <SidebarMenuItem key={item.title} data-onboarding={idx === 0 ? "sidebar-presta-infos" : idx === 1 ? "sidebar-presta-competences" : undefined}>
                  <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                    <NavLink 
                      to={item.url}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                      onClick={handleNavigation}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Sous-menu Estimation IA */}
              {isMobile ? (
                <SidebarMenuItem data-onboarding="sidebar-presta-estimation">
                  <SidebarMenuButton 
                    onClick={() => setMobileSubmenu({ title: "ESTIMATION IA", items: estimationIASubmenu.items })}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200"
                  >
                    <estimationIASubmenu.icon className="h-5 w-5 shrink-0" />
                    <span className="text-sm">{estimationIASubmenu.title}</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : (
                <Collapsible
                  open={openEstimationMenu}
                  onOpenChange={setOpenEstimationMenu}
                  className="group/collapsible"
                >
                  <SidebarMenuItem data-onboarding="sidebar-presta-estimation">
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton 
                        tooltip={state === "collapsed" ? estimationIASubmenu.title : undefined}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${
                          currentPath.startsWith('/prestataire/estimation') ? "bg-[#F57C00]/20 text-[#F57C00] font-medium" : ""
                        }`}
                      >
                        <estimationIASubmenu.icon className="h-5 w-5 shrink-0" />
                        {state !== "collapsed" && (
                          <>
                            <span className="text-sm">{estimationIASubmenu.title}</span>
                            <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 text-gray-400" />
                          </>
                        )}
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub className="border-l border-white/10 ml-6 pl-3 mt-1">
                        {estimationIASubmenu.items.map((subItem) => (
                          <SidebarMenuSubItem key={subItem.title}>
                            <SidebarMenuSubButton asChild>
                              <NavLink 
                                to={subItem.url}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClassName(subItem.url)}`}
                                onClick={handleNavigation}
                              >
                                <subItem.icon className="h-4 w-4 shrink-0" />
                                <span className="text-sm">{subItem.title}</span>
                              </NavLink>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              )}

              {/* Items après Estimation IA */}
              {navigationItems.prestatairePersonal.slice(4).map((item, idx) => (
                <SidebarMenuItem key={item.title} data-onboarding={idx === 0 ? "sidebar-presta-missions" : idx === 1 ? "sidebar-presta-calendrier" : undefined}>
                  <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                    <NavLink 
                      to={item.url}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                      onClick={handleNavigation}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* Navigation Dashboard simple pour clients (non-prestataires) */}
      {effectiveRole === 'client' && navigationItems.authenticated && navigationItems.authenticated.length > 0 && (
        <SidebarGroup>
          <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
            Mon Espace
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.authenticated.map((item) => (
                <SidebarMenuItem key={item.title} data-onboarding="sidebar-client-dashboard">
                  <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                    <NavLink 
                      to={item.url}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                      onClick={handleNavigation}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* Navigation Espace Personnel Client */}
      {navigationItems.clientPersonal && navigationItems.clientPersonal.length > 0 && (
        <>
          <SidebarGroup>
            <SidebarGroupLabel className={`${state === "collapsed" ? "sr-only" : ""} text-[#F57C00] text-xs font-semibold uppercase tracking-wider`}>
              Mon Profil
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {/* Sous-menu Mes demandes */}
                {isMobile ? (
                  <SidebarMenuItem data-onboarding="sidebar-client-demandes">
                    <SidebarMenuButton 
                      onClick={() => setMobileSubmenu({ title: "MES DEMANDES", items: clientDemandesSubmenu.items })}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                        currentPath.startsWith('/client/add-mission') || currentPath.startsWith('/client/recap-missions') 
                          ? "bg-[#F57C00]/20 text-[#F57C00] font-medium" 
                          : "text-gray-300 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <clientDemandesSubmenu.icon className="h-5 w-5 shrink-0" />
                      <span className="text-sm">{clientDemandesSubmenu.title}</span>
                      <ChevronRight className="ml-auto h-4 w-4 text-gray-400" />
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : (
                  <Collapsible
                    open={openDemandesMenu}
                    onOpenChange={setOpenDemandesMenu}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem data-onboarding="sidebar-client-demandes">
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton 
                          tooltip={state === "collapsed" ? clientDemandesSubmenu.title : undefined}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${
                            currentPath.startsWith('/client/add-mission') || currentPath.startsWith('/client/recap-missions') 
                              ? "bg-[#F57C00]/20 text-[#F57C00] font-medium" 
                              : "text-gray-300 hover:bg-white/10 hover:text-white"
                          }`}
                        >
                          <clientDemandesSubmenu.icon className="h-5 w-5 shrink-0" />
                          {state !== "collapsed" && (
                            <>
                              <span className="text-sm">{clientDemandesSubmenu.title}</span>
                              <ChevronDown className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180 text-gray-400" />
                            </>
                          )}
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub className="border-l border-white/10 ml-6 pl-3 mt-1">
                          {clientDemandesSubmenu.items.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.title}>
                              <SidebarMenuSubButton asChild>
                                <NavLink 
                                  to={subItem.url}
                                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 ${getNavClassName(subItem.url)}`}
                                  onClick={handleNavigation}
                                >
                                  <subItem.icon className="h-4 w-4 shrink-0" />
                                  <span className="text-sm">{subItem.title}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                )}

                {/* Autres items du profil client */}
                {navigationItems.clientPersonal.map((item, idx) => (
                  <SidebarMenuItem key={item.title} data-onboarding={idx === 0 ? "sidebar-client-infos" : idx === 1 ? "sidebar-client-carte" : idx === 3 ? "sidebar-client-support" : undefined}>
                    <SidebarMenuButton asChild tooltip={state === "collapsed" ? item.title : undefined}>
                      <NavLink 
                        to={item.url}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${state === "collapsed" ? "justify-center px-0" : ""} ${getNavClassName(item.url)}`}
                        onClick={handleNavigation}
                      >
                        <item.icon className="h-5 w-5 shrink-0" />
                        {state !== "collapsed" && <span className="text-sm">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </>
      )}

    </>
  );

  return (
    <Sidebar
      collapsible="icon"
      className="bg-[#0A2540] border-r border-white/10 flex flex-col h-full"
    >
      {/* Header avec logo */}
      <SidebarHeader className="p-4 border-b border-white/10">
        <NavLink to="/" className="flex items-center space-x-3">
          <div className="w-9 h-9 bg-[#F57C00] rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">Q</span>
          </div>
          {state !== "collapsed" && (
            <span className="text-lg font-bold text-white tracking-tight">QuiQuoiOùQuand</span>
          )}
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="flex-1 overflow-y-auto px-2 py-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
        {navigationItems.isPublic ? renderPublicSidebar() : renderAuthenticatedSidebar()}

        {/* Connexion/Déconnexion */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={state === "collapsed" ? (initialized && isAuthenticated() ? "Déconnexion" : "Connexion") : undefined}>
                  {initialized && isAuthenticated() ? (
                    <button 
                      onClick={handleSignOut}
                      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-gray-300 hover:bg-red-500/20 hover:text-red-400 transition-all duration-200"
                    >
                      <LogOut className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm">Déconnexion</span>}
                    </button>
                  ) : (
                    <button 
                      onClick={() => {
                        navigate('/auth?origin=nav');
                        if (isMobile) {
                          setOpenMobile(false);
                        }
                      }}
                      className="flex items-center justify-center gap-3 w-full px-3 h-10 rounded-lg bg-[#F57C00] text-white hover:bg-[#E65100] transition-all duration-200"
                    >
                      <LogIn className="h-5 w-5 shrink-0" />
                      {state !== "collapsed" && <span className="text-sm font-medium leading-none">Connexion</span>}
                    </button>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer avec informations utilisateur */}
      {initialized && isAuthenticated() && (
        <SidebarFooter className="p-4 border-t border-white/10">
          <div className="flex items-center space-x-3">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-[#F57C00] text-white text-xs font-medium">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            {state !== "collapsed" && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {userData?.first_name || getFirstName()} {userData?.last_name || getLastName()}
                </p>
                {getUserRoleLabel() && (
                  <p className="text-xs text-gray-400 truncate">
                    {getUserRoleLabel()}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {roles && roles.length > 0 && (() => {
                    // Priorité: admin > prestataire > client
                    const priorityOrder = ['admin', 'prestataire', 'client'];
                    const prioritizedRole = roles.find(r => r.role === 'admin') 
                      || roles.find(r => r.role === 'prestataire') 
                      || roles[0];
                    const displayStatus = prioritizedRole?.status || roles[0].status;
                    
                    return (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        displayStatus === 'actif' || displayStatus === 'active' ? 'bg-green-500/20 text-green-400' :
                        displayStatus === 'prospect' ? 'bg-blue-500/20 text-blue-400' :
                        displayStatus === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {displayStatus}
                      </span>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </SidebarFooter>
      )}
      
      {/* Modale de connexion par téléphone */}
      <PhoneLoginDialog 
        open={showPhoneLogin} 
        onOpenChange={setShowPhoneLogin} 
      />

      {/* Panneau de sous-menu mobile (navigation à 2 niveaux) */}
      <MobileSubmenuPanel
        isOpen={!!mobileSubmenu}
        onClose={() => setMobileSubmenu(null)}
        title={mobileSubmenu?.title || ""}
        items={mobileSubmenu?.items || []}
        onNavigate={handleNavigation}
        getNavClassName={getNavClassName}
      />
    </Sidebar>
  );
}
